'use client';

import React, { useEffect, useMemo } from 'react';
import { Conditional } from 'components/pcc-components';
import { LocationAddress } from 'services/location/types';
import { DealerResponse } from 'services/dealer';
import {
  DealerAssociationResponse,
  DealerErrorResponse
} from 'services/dealer-association';
import LoaderOverlay from 'components/loader-overlay';
import { useAssociatedDealerForms } from 'components/associated-dealers/hooks/use-associated-dealer-forms';
import { SelectCustomerForm } from '../select-customer-form/select-customer-form';
import styles from './associated-dealer-modal.module.scss';
import { useAssociatedDealer } from 'components/associated-dealers/associated-dealer-context';
import { AssociatedDealerWrapper } from './associated-dealer-wrapper';

type AssociatedDealerModalContentProps = {
  isLoggedIn: boolean;
  isDealerUser: boolean;
  isInstantAccessUser: boolean;
  isCspUser: boolean;
  dealerUserResponse: DealerResponse;
  registeredUserResponse: DealerAssociationResponse | DealerErrorResponse;
  storeAddress: LocationAddress;
  openAdpModal: boolean;
  showSkipThisStep: boolean;
  entitlementResponse?: {
    redirectURL: string;
    isMicoCookieRequired: boolean;
  };
};

const AssociatedDealerModalContent = ({
  isLoggedIn,
  isDealerUser,
  isCspUser,
  isInstantAccessUser,
  dealerUserResponse,
  registeredUserResponse,
  storeAddress,
  openAdpModal,
  showSkipThisStep,
  entitlementResponse
}: AssociatedDealerModalContentProps) => {
  const { isLoading, associatedDealerModal, onSuccessHandler } =
    useAssociatedDealer();
  const { dealerSearchForm, selectCustomerForm } = useAssociatedDealerForms();

  const isAdpModalActive = useMemo(
    () =>
      !isLoading &&
      !associatedDealerModal.modalControl.isActive &&
      openAdpModal,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openAdpModal]
  );

  /**
   * * if user changes esite by modifying the URL, they will be in the wrong site,
   * * this prompts the user to select a location so they get redirected to the correct esite
   */
  useEffect(() => {
    if (isAdpModalActive) {
      associatedDealerModal.modalControl.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdpModalActive]);

  if (entitlementResponse) {
    onSuccessHandler(entitlementResponse);
    return;
  }

  return (
    <div id="ADP-Trigger">
      <AssociatedDealerWrapper
        isDealerUser={isDealerUser}
        dealerUserResponse={dealerUserResponse}
      >
        <SelectCustomerForm
          isModal={true}
          dealerSearchForm={dealerSearchForm}
          selectCustomerForm={selectCustomerForm}
          isDealerUser={isDealerUser}
          isInstantAccessUser={isInstantAccessUser}
          isCspUser={isCspUser}
          isLoggedIn={isLoggedIn}
          dealerUserResponse={dealerUserResponse}
          registeredUserResponse={registeredUserResponse}
          storeAddress={storeAddress}
          wrongSite={openAdpModal}
          showSkipThisStep={showSkipThisStep}
          showPricingWarning={false}
        />
      </AssociatedDealerWrapper>
      <Conditional test={isLoading}>
        <LoaderOverlay classNames={styles['adpmodal-loader-overlay']} />
      </Conditional>
    </div>
  );
};

export default React.memo(AssociatedDealerModalContent);
